<script lang="ts">
	import { onMount } from 'svelte'
	import {
		type ApplePay as BraintreeApplePayInstance,
		applePay as braintreeApplePay,
		client as braintreeClient
	} from 'braintree-web'
	import { goto } from '$app/navigation'
	import { appState } from '$lib/app-state.svelte'
	import { cartItems } from '$lib/stores.svelte'

	interface Props {
		onsuccess: () => void
	}

	let { onsuccess }: Props = $props()

	let applePaySession: ApplePaySession
	let braintreeApplePayInstance: BraintreeApplePayInstance
	let applePaymentRequest: ApplePayJS.ApplePayPaymentRequest
	let showApplePay = $state(false) // hide until all checks pass
	const order: Order = {
		payment: {
			date: new Date(),
			amount: '0.00',
			expirationDate: '',
			cardType: '',
			maskedNumber: '',
			nonce: undefined,
			id: ''
		},
		items: [],
		customer: {
			id: 0,
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			address: {
				streetAddress: '',
				city: '',
				state: '',
				zipCode: ''
			}
		},
		isGift: false,
		sendVia: 'email',
		recipient: {
			id: 0,
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			address: {
				firstName: '',
				lastName: '',
				streetAddress: '',
				city: '',
				state: '',
				zipCode: ''
			}
		},
		giftMessage: '',
		instructions: ''
	}

	// Initialize Apple Pay instance and check availability
	onMount(async () => {
		showApplePay = applePayPossible() // Not SSR-compatible
		if (showApplePay) {
			try {
				braintreeApplePayInstance = await createApplePayInstance(appState.clientToken)
				if (!braintreeApplePayInstance.merchantIdentifier) {
					throw new Error('Missing merchant identifier.')
				}
				// Even though next line shows as deprecated, Braintree isn't compatible with the updated approach.
				showApplePay = await ApplePaySession.canMakePaymentsWithActiveCard(
					braintreeApplePayInstance.merchantIdentifier
				)
				// const applePayCapabilities = await ApplePaySession.applePayCapabilities(
				// 	braintreeApplePayInstance.merchantIdentifier
				// )
				// showApplePay = applePayCapabilities.paymentCredentialStatus !== 'applePayUnsupported'
			} catch (error) {
				console.error('Error creating Apple Pay instance.', error)
				showApplePay = false
			}
		}
	})

	const applePayPossible = (): boolean =>
		location.protocol === 'https:' &&
		(ApplePaySession?.supportsVersion?.(3) ?? false) &&
		(ApplePaySession?.canMakePayments?.() ?? false)

	const createApplePayInstance = async (token: string) => {
		const client = await braintreeClient.create({
			authorization: token
		})
		return braintreeApplePay.create({ client }) // braintree Apple Pay
	}

	// Convert cartItems to Apple Pay lineItems
	const toApplePayLineItems = (items: CartItem[]): ApplePayJS.ApplePayLineItem[] => {
		return items.map(item => ({
			label: `${item.quantity}x ${item.product.name}`,
			amount: `${item.quantity * item.product.price}`
		}))
	}

	const getGrandTotal = (items: CartItem[]): number =>
		items.reduce((sum, item) => sum + item.product.price * item.quantity, 0)

	// Returns applePaymentRequest with our defaults
	const applePayCreatePaymentRequest = (braintreeApplePayInstance: BraintreeApplePayInstance) => {
		const requiredBillingContactFields: ApplePayJS.ApplePayContactField[] = [
			'name',
			'postalAddress',
			'phone',
			'email'
		]
		const shippingMethods: ApplePayJS.ApplePayShippingMethod[] = [
			{
				label: 'Purchased for myself.',
				detail: 'Register under my name.',
				amount: '0.00',
				identifier: 'Email'
			},
			{
				label: 'This is a gift.',
				detail: 'Email to shipping contact.',
				amount: '0.00',
				identifier: 'Gift-Email'
			},
			{
				label: 'This is a gift',
				detail: 'Mail to shipping contact',
				amount: '0.00',
				identifier: 'Gift-Mail-Recipient'
			},
			{
				label: 'This is a gift.',
				detail: 'Mail to me.',
				amount: '0.00',
				identifier: 'Gift-Mail-Sender'
			}
		]
		const total: ApplePayJS.ApplePayLineItem = {
			label: 'Schoolhouse Yoga',
			amount: `${totalAmount}`
		}
		const paymentRequest: BraintreeApplePayPaymentRequest = {
			requiredBillingContactFields,
			requiredShippingContactFields: requiredBillingContactFields,
			shippingMethods,
			lineItems,
			total
		}
		return braintreeApplePayInstance.createPaymentRequest(
			paymentRequest
		) as ApplePayJS.ApplePayPaymentRequest
	}

	const onValidateMerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
		try {
			const merchantSession = await braintreeApplePayInstance.performValidation({
				validationURL: event.validationURL,
				displayName: 'Schoolhouse Yoga, Inc.'
			})
			applePaySession.completeMerchantValidation(merchantSession)
		} catch (error) {
			console.error('Error validating Apple Pay merchant.', error)
			applePaySession.abort()
		}
	}

	const onShippingMethodSelected = (event: ApplePayJS.ApplePayShippingMethodSelectedEvent) => {
		if (!applePaymentRequest.lineItems)
			throw new Error('The Apple Payment request is missing line items.')
		order.isGift = event.shippingMethod.identifier.startsWith('Gift')
		order.sendVia = event.shippingMethod.identifier.endsWith('Email') ? 'email' : 'mail'
		order.instructions = `${event.shippingMethod.label} ${event.shippingMethod.detail}`
		applePaySession.completeShippingMethodSelection(
			ApplePaySession.STATUS_SUCCESS,
			applePaymentRequest.total,
			applePaymentRequest.lineItems
		)
	}

	const extractCustomerAndRecipient = (paymentEvent: ApplePayJS.ApplePayPayment) => {
		const { billingContact, shippingContact } = paymentEvent

		if (!billingContact || !shippingContact) {
			throw new Error('Billing and shipping contacts are required.')
		}

		order.customer = {
			id: 0, // Needed by our Order.Customer - not Apple or Braintree
			firstName: billingContact.givenName!,
			lastName: billingContact.familyName!,
			email: shippingContact.emailAddress!, // billing email restricted by Apple Pay
			phone: shippingContact.phoneNumber!, // billing phone restricted by Apple Pay
			address: {
				streetAddress: billingContact.addressLines![0],
				city: billingContact.locality!,
				state: billingContact.administrativeArea!,
				zipCode: billingContact.postalCode!
			}
		}
		order.recipient = {
			id: 0, // Needed by our Order.Customer - not Apple or Braintree
			firstName: shippingContact.givenName!,
			lastName: shippingContact.familyName!,
			email: shippingContact.emailAddress!,
			phone: shippingContact.phoneNumber,
			address: {
				firstName: shippingContact.givenName!,
				lastName: shippingContact.familyName!,
				streetAddress: shippingContact.addressLines![0],
				city: shippingContact.locality!,
				state: shippingContact.administrativeArea!,
				zipCode: shippingContact.postalCode!
			}
		}
	}

	const onPaymentAuthorized = async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
		let paymentSuccess = false

		try {
			// Tokenize the payment
			const { nonce, details } = await braintreeApplePayInstance.tokenize({
				token: event.payment.token
			})
			order.payment.nonce = nonce
			order.payment.cardType = details.cardType
			order.payment.maskedNumber = `APPLPAY*******${details.dpanLastTwo}`
			paymentSuccess = true

			// Extract customer and recipient information
			extractCustomerAndRecipient(event.payment)

			order.items = JSON.parse(JSON.stringify($cartItems))

			// Submit the order
			await submitApplePayOrder()

			// Complete payment with success
			applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS)
		} catch (err) {
			console.error('Error during Apple Pay payment processing:', err)

			// Complete payment with failure
			applePaySession.completePayment(ApplePaySession.STATUS_FAILURE)

			appState.showToast({
				tone: 'danger',
				title: 'Order did not go through',
				message:
					'Apple Pay did not go through due to an issue on our end. Please call us at +1-412-401-4444 for assistance or try a regular credit card transaction.'
			})
		}
	}

	// Button handler
	const applePayCheckout = async () => {
		if (!braintreeApplePayInstance || !$cartItems || $cartItems.length === 0) return

		applePaymentRequest = applePayCreatePaymentRequest(braintreeApplePayInstance)

		try {
			applePaySession = new ApplePaySession(3, applePaymentRequest)
			if (!applePaySession) {
				throw new Error('Failed to create Apple Pay session using the Apple Pay Request.')
			}
			applePaySession.onvalidatemerchant = onValidateMerchant
			applePaySession.onshippingmethodselected = onShippingMethodSelected
			applePaySession.onpaymentauthorized = onPaymentAuthorized
			applePaySession.begin()
		} catch (error) {
			console.error('Error creating Apple Pay session.', error)
		}
	}

	const submitApplePayOrder = async () => {
		const url = `/api/payment/order`
		const response: Response = await fetch(url, {
			method: 'POST',
			body: JSON.stringify(order),
			headers: { 'Content-Type': 'application/json' }
		})
		if (!response.ok) {
			const { message } = await response.json()
			console.error('Error posting order: ', message)
			return
		}

		// If no error, put results in orderConfirmation store
		appState.orderConfirmation = await response.json()

		// Clear out local storage and store
		localStorage.removeItem('cartItems')
		$cartItems = []

		onsuccess()

		// Go to confirmation page
		await goto('/confirmation')
	}

	const totalAmount = $derived(getGrandTotal($cartItems))
	const lineItems = $derived(toApplePayLineItems($cartItems))
</script>

{#if showApplePay}
	<button
		onclick={applePayCheckout}
		class="apple-pay-button tw:mt-3 tw:h-10 tw:w-full tw:inline-block tw:!rounded-md"
		aria-label="Apple Pay checkout"
		title="Apple Pay checkout"></button>
	<p>Class credits are applied to Apple Pay email.</p>
{/if}

<style>
	@supports (-webkit-appearance: -apple-pay-button) {
		.apple-pay-button {
			appearance: -apple-pay-button;
			-webkit-appearance: -apple-pay-button;
			-apple-pay-button-type: check-out;
		}
	}
</style>
